import React, {useState, useEffect, useRef, useMemo} from "react"
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Alert,
    FormFeedback,
    Collapse
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { createPortal } from "react-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { createInstantLink } from "../../../../api/requests/payment-requests";
import { canCreateInstallmentGroup } from "../../../../constants/roles";
import { useAuth } from "../../../../hooks/useAuth";
import { useAccounts } from "../../../../hooks/useAccounts";
import { useCariGroups } from "../../../../hooks/useCariGroups";
import AddGroupModal from "./../../AddGroupModal";
import AdminBillingOptions from "./AdminBillingOptions";
import InstallmentRateCards from "./InstallmentRateCards";
import DateTimeInput, { nowLocalDateTime } from "../../../../components/Common/DateTimeInput";

const showToast = (type, msg) => {
    const map = { danger: "error", error: "error" };
    const fn = toast[map[type] || type] || toast.info;
    fn(msg, { autoClose: 3000 });
};
const getDefaultExpiryDate = () =>
    new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16);

export default function InstantPaymentModal({
                                                isOpen,
                                                toggle,
                                                companyId,
                                                onSaved,
                                                onSuccess
                                            }) {
    const [error, setError] = useState("");
    const [link, setLink] = useState("");
    const [showRates, setShowRates] = useState(false);
    const [displayFixedAmount, setDisplayFixedAmount] = useState("");
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [filteredGroups, setFilteredGroups] = useState([]);
    const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);

    const timerRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);

    const { roles, loadingUser } = useAuth();
    const isAdmin =
        roles.includes("ROLE_SUPER_ADMIN") || roles.includes("ROLE_COMPANY_ADMIN");
    const { accounts } = useAccounts(isOpen, isAdmin);
    const {
        cariGroups,
        loadingGroups,
        error: groupsError, // submit error'u ile karışmasın
        refetchGroups
    } = useCariGroups(isOpen, companyId);

    // Mobil tespiti
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 576);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const canAddGroup = canCreateInstallmentGroup(roles);

    const handleFixedAmountChange = (e) => {
        // ... handleFixedAmountChange fonksiyonunun içeriği (değişiklik yok)
        const raw = (e.target.value || "").trim();

        const hasComma = raw.includes(",");
        const dotMatches = raw.match(/\./g) || [];
        const hasDot = dotMatches.length > 0;

        let intPartRaw = raw;
        let fracPartRaw = "";
        let hadExplicitDecimal = false;

        if (hasComma) {
            hadExplicitDecimal = true;
            const m = raw.match(/^(.*?),(.*)$/);
            intPartRaw = m ? m[1] : raw;
            fracPartRaw = m ? m[2] : "";
        } else if (hasDot) {
            if (dotMatches.length === 1 && /\.\d{0,2}$/.test(raw)) {
                hadExplicitDecimal = true;
                const m = raw.match(/^(.*?)\.(.*)$/);
                intPartRaw = m ? m[1] : raw;
                fracPartRaw = m ? m[2] : "";
            } else {
                hadExplicitDecimal = false;
                intPartRaw = raw;
                fracPartRaw = "";
            }
        }

        const intDigits = (intPartRaw || "").replace(/\D/g, "");
        const fracDigits = (fracPartRaw || "").replace(/\D/g, "").slice(0, 2);

        let normalized = "";
        if (intDigits.length) {
            const intNoLeadingZeros = String(parseInt(intDigits, 10));
            normalized = intNoLeadingZeros === "NaN" ? "" : intNoLeadingZeros;
        }
        if (hadExplicitDecimal) {
            if (!normalized) normalized = "0";
            normalized += "." + fracDigits;
        }

        const groupThousands = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        const groupedInt = intDigits
            ? groupThousands(intDigits)
            : hadExplicitDecimal
                ? "0"
                : "";

        let display = groupedInt;
        if (hadExplicitDecimal) {
            if (raw.endsWith(",") || raw.endsWith(".")) {
                display = `${groupedInt},`;
            } else {
                display = `${groupedInt},${fracDigits}`;
            }
        }

        formik.setFieldValue("fixedAmount", normalized);
        setDisplayFixedAmount(display);
    };

    // Sadece görünür alanları doğrulayan submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        const visibleFields = ["accountGroupId", "expiryDate"];

        if (isAdmin && formik.values.billingOption === "existing_account") {
            visibleFields.push("billingOption", "accountId");
        } else if (isAdmin && formik.values.billingOption === "new_invoice") {
            visibleFields.push(
                "billingOption",
                "invoiceAccountType",
                "invoiceCompanyName",
                "invoiceTaxOffice",
                "invoiceAddress",
                "invoiceEmail",
                "invoicePhone"
            );
            if (formik.values.invoiceAccountType === "SOLE_PROPRIETORSHIP") {
                visibleFields.push("invoiceTcNumber");
            } else if (formik.values.invoiceAccountType) {
                visibleFields.push("invoiceTaxNumber");
            }
        } else if (isAdmin) {
            visibleFields.push("billingOption");
        }

        const errors = await formik.validateForm();
        const touchedFields = {};
        visibleFields.forEach((f) => (touchedFields[f] = true));
        formik.setTouched(touchedFields);

        const visibleErrors = {};
        visibleFields.forEach((f) => {
            if (errors[f]) visibleErrors[f] = errors[f];
        });

        if (Object.keys(visibleErrors).length > 0) {
            const firstError = visibleErrors[Object.keys(visibleErrors)[0]];
            showToast("warning", firstError);
        } else {
            formik.handleSubmit(e);
        }
    };

    const handleAccountChange = (e) => {
        const accountId = e.target.value;
        formik.setFieldValue("accountId", accountId);
        const acc = accounts.find((a) => a.id === accountId);
        setSelectedAccount(acc || null);
    };

    const initialValues = useMemo(() => ({
        accountGroupId: "",
        expiryDate: getDefaultExpiryDate(),
        fixedAmount: "",
        billingOption: "",
        accountId: "",
        invoiceAccountType: "",
        invoiceCompanyName: "",
        invoiceTaxNumber: "",
        invoiceTcNumber: "",
        invoiceTaxOffice: "",
        invoiceAddress: "",
        invoiceEmail: "",
        invoicePhone: ""
    }), []);

    useEffect(() => {
        if (!isOpen) return;
        setLink("");
        setError("");
        setDisplayFixedAmount("");
        setShowRates(false);
        setSelectedAccount(null);
        formik.resetForm({
            values: {
                ...initialValues, // Memoize edilmiş objeyi baz al
                expiryDate: getDefaultExpiryDate() // Ama tarihi her açılışta güncelle
            }
        });
    }, [isOpen, initialValues]);
    const formik = useFormik({
        enableReinitialize: false,
        initialValues: initialValues,
        validationSchema: Yup.object({
            accountGroupId: Yup.string().required("Grup seçiniz"),
            expiryDate: Yup.string().required("Bitiş tarihi giriniz"),
            ...(isAdmin
                ? {
                    billingOption: Yup.string().required(
                        "Lütfen bir seçenek belirleyin"
                    ),
                    accountId: Yup.string().when("billingOption", {
                        is: "existing_account",
                        then: (s) => s.required("Cari/Bayi seçiniz"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceAccountType: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.required("Cari/Bayi tipi seçiniz"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceCompanyName: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.required("Şirket adı gereklidir"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceTaxOffice: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.required("Vergi dairesi gereklidir"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceAddress: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.required("Adres gereklidir"),
                        otherwise: (s) => s.notRequired()
                    }),
                    fixedAmount: Yup.number()
                        .typeError("Geçerli bir tutar giriniz")
                        .positive("Tutar 0'dan büyük olmalıdır")
                        .nullable()
                        .transform((v, o) => (String(o).trim() === "" ? null : v)),
                    invoiceEmail: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) => s.email("Geçerli bir email adresi giriniz"),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoicePhone: Yup.string().when("billingOption", {
                        is: "new_invoice",
                        then: (s) =>
                            s.matches(
                                /^[0-9+\\-\\s\\(\\)]+$/,
                                "Geçerli bir telefon numarası giriniz"
                            ),
                        otherwise: (s) => s.notRequired()
                    }),
                    invoiceTcNumber: Yup.string().trim().when(
                        ["billingOption", "invoiceAccountType"],
                        {
                            is: (bo, at) =>
                                bo === "new_invoice" && at === "SOLE_PROPRIETORSHIP",
                            then: (s) =>
                                s.required("TC kimlik numarası gereklidir").matches(/^\d{11}$/, "TC kimlik numarası 11 haneli olmalıdır"),
                            otherwise: (s) => s.notRequired()
                        }
                    ),
                    invoiceTaxNumber: Yup.string().when(
                        ["billingOption", "invoiceAccountType"],
                        {
                            is: (bo, at) =>
                                bo === "new_invoice" && at && at !== "SOLE_PROPRIETORSHIP",
                            then: (s) =>
                                s
                                    .required("Vergi numarası gereklidir")
                                    .matches(/^\d{10}$/, "Vergi numarası 10 haneli olmalıdır"),
                            otherwise: (s) => s.notRequired()
                        }
                    )
                }
                : {})
        }),
        onSubmit: async (values, { setSubmitting }) => {
            setError("");
            setLink("");
            try {
                const group = cariGroups.find((g) => g.id === values.accountGroupId);
                const cardsMaxInstallments = {};
                (group.installmentRates || []).forEach((r) => {
                    if (r.enabled)
                        cardsMaxInstallments[r.cardBrand] = Math.max(
                            cardsMaxInstallments[r.cardBrand] || 0,
                            r.installmentCount
                        );
                });
                const dto = {
                    companyId,
                    accountGroupId: values.accountGroupId,
                    cardsMaxInstallments,
                    expiryDate: values.expiryDate,
                    ...(String(values.fixedAmount ?? "").trim() !== ""
                        ? { fixedAmount: Number(values.fixedAmount) }
                        : {}),
                    ...(isAdmin &&
                        values.billingOption === "existing_account" && {
                            accountId: values.accountId
                        }),
                    ...(isAdmin &&
                        values.billingOption === "new_invoice" && {
                            invoiceAccountType: values.invoiceAccountType,
                            invoiceCompanyName: values.invoiceCompanyName,
                            invoiceTaxNumber: values.invoiceTaxNumber,
                            invoiceTcNumber: values.invoiceTcNumber,
                            invoiceAddress: values.invoiceAddress,
                            invoiceTaxOffice: values.invoiceTaxOffice,
                            ...(values.invoiceEmail && {
                                invoiceEmail: values.invoiceEmail
                            }),
                            ...(values.invoicePhone && {
                                invoicePhone: values.invoicePhone
                            })
                        })
                };
                const resp = await createInstantLink(dto);
                const json = await resp.json();
                if (resp.ok) {
                    setLink(json.data);
                    showToast(
                        "success",
                        "Oluşturulan bağlantı, “Ödeme Linkleri” sayfasına eklendi"
                    );
                    onSuccess && onSuccess();
                    onSaved && onSaved();
                    timerRef.current = setTimeout(() => {
                        toggle();
                    }, 2000);
                } else {
                    setError(json.message || "Link oluşturulamadı");
                }
            } catch (err) {
                console.error(err);
                setError("Link oluşturulurken hata oluştu");
            } finally {
                setSubmitting(false);
            }
        }
    });

    // Modal açıldığında reset
    useEffect(() => {
        if (!isOpen) return;
        setLink("");
        setError("");
        setDisplayFixedAmount("");
        setShowRates(false);
        setSelectedAccount(null);
        formik.resetForm({
            values: {
                ...formik.initialValues,
                expiryDate: getDefaultExpiryDate() // Resetlerken tarihi güncelle
            }
        });
    }, [isOpen]);

    // Gruplar yüklenince varsayılan grup
    useEffect(() => {
        if (cariGroups.length > 0 && !formik.values.accountGroupId) {
            // Eğer 'existing_account' seçili değilse ilk grubu ata
            if (formik.values.billingOption !== "existing_account") {
                formik.setFieldValue("accountGroupId", cariGroups[0].id);
            }
        }
    }, [cariGroups, formik.values.billingOption]); // billingOption'a da bağlı

    // Cari → grup filtreleme
    useEffect(() => {
        if (
            !selectedAccount ||
            formik.values.billingOption !== "existing_account"
        ) {
            setFilteredGroups(cariGroups);
            if (formik.values.billingOption !== "existing_account") {
                // Otomatik seçimi engellemek için resetle
                // formik.setFieldValue("accountGroupId", ""); // Bu satır sorun yaratabilir, yukarıdaki useEffect ile çakışıyor.
            }
            return;
        }
        if (selectedAccount.accountGroupId) {
            const onlyGroup = cariGroups.filter(
                (g) => g.id === selectedAccount.accountGroupId
            );
            setFilteredGroups(onlyGroup);
            formik.setFieldValue(
                "accountGroupId",
                onlyGroup.length === 1 ? onlyGroup[0].id : ""
            );
        } else {
            setFilteredGroups([]);
            formik.setFieldValue("accountGroupId", "");
        }
    }, [selectedAccount, cariGroups, formik.values.billingOption]);

    // Flow helpers
    const flowChosen = !isAdmin || !!formik.values.billingOption;
    const usingExisting =
        isAdmin && formik.values.billingOption === "existing_account";
    const usingNewInvoice =
        isAdmin && formik.values.billingOption === "new_invoice";

    // Timeout temizleme
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleResetAccount = () => {
        formik.setFieldValue("accountId", "");
        setSelectedAccount(null);
    };

    const handleBillingOptionChange = (e) => {
        const newOption = e.target.value;

        // touched ve temizlenecek alanları hesapla
        const newTouched = { ...formik.touched };
        const cleared = {};

        if (newOption === "existing_account") {
            // fatura alanlarını temizle
            Object.keys(formik.values)
                .filter((k) => k.startsWith("invoice"))
                .forEach((k) => {
                    cleared[k] = "";
                    newTouched[k] = false;
                });
            newTouched.accountId = false;
        } else if (newOption === "new_invoice") {
            // cari alanını temizle
            cleared.accountId = "";
            newTouched.accountId = false;
            Object.keys(formik.values)
                .filter((k) => k.startsWith("invoice"))
                .forEach((k) => (newTouched[k] = false));
        } else {
            // ikisini de temizle
            cleared.accountId = "";
            Object.keys(formik.values)
                .filter((k) => k.startsWith("invoice"))
                .forEach((k) => {
                    cleared[k] = "";
                    newTouched[k] = false;
                });
            newTouched.accountId = false;
        }

        // TEK bir setValues ile güncelle (previous values üzerinden)
        formik.setValues((prev) => ({
            ...prev,
            billingOption: newOption,          // <<< kritik: yeni seçenek burada yazılıyor
            accountGroupId: "",                // grup seçimini her zaman sıfırla
            ...cleared,                        // temizlenen alanlar
        }));

        formik.setTouched(newTouched);
        setSelectedAccount(null); // gerekiyorsa
    };

    const handleAccountTypeChange = (e) => {
        const accountType = e.target.value;
        formik.setFieldValue("invoiceAccountType", accountType);
        formik.setFieldValue("invoiceTaxNumber", "");
        formik.setFieldValue("invoiceTcNumber", "");
        formik.setTouched({
            ...formik.touched,
            invoiceTaxNumber: false,
            invoiceTcNumber: false
        });
    };

    const currentGroup = React.useMemo(() => {
        return cariGroups.find((g) => g.id === formik.values.accountGroupId);
    }, [cariGroups, formik.values.accountGroupId]);

    return (
        <>
            <Modal
                isOpen={isOpen}
                toggle={toggle}
                size={isMobile ? "" : "xl"}
                style={{
                    height: isMobile ? "85vh" : "95vh",
                    margin: isMobile ? "7.5vh auto" : "2.5vh auto",
                    maxWidth: isMobile ? "95vw" : undefined,
                    width: isMobile ? "95vw" : undefined
                }}
            >
                {createPortal(
                    <ToastContainer
                        position="top-right"
                        autoClose={3000}
                        hideProgressBar={false}
                        newestOnTop={false}
                        closeOnClick
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        theme="colored"
                        style={{ zIndex: 20000 }}
                    />,
                    document.body
                )}
                <ModalHeader toggle={toggle}>Ödeme Linki Oluştur</ModalHeader>
                <ModalBody
                    style={{
                        height: isMobile ? "calc(85vh - 120px)" : "calc(95vh - 120px)",
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingRight: isMobile ? "8px" : "15px"
                    }}
                >
                    {loadingUser ? (
                        <div className="text-center py-3">
                            <Spinner />
                        </div>
                    ) : (
                        isAdmin && (
                            <AdminBillingOptions
                                formik={formik}
                                accounts={accounts}
                                usingExisting={usingExisting}
                                usingNewInvoice={usingNewInvoice}
                                handleBillingOptionChange={handleBillingOptionChange}
                                handleAccountChange={handleAccountChange}
                                handleAccountTypeChange={handleAccountTypeChange}
                                handleResetAccount={handleResetAccount}
                            />
                        )
                    )}

                    {!flowChosen && isAdmin ? (
                        <Alert color="info">
                            Devam etmek için önce <strong>“Cari/Fatura Seçenekleri”</strong>{" "}
                            alanından bir seçim yapın.
                        </Alert>
                    ) : loadingGroups ? (
                        <div className="text-center py-3">
                            <Spinner />
                        </div>
                    ) : cariGroups.length === 0 ? (
                        <Alert color="warning">
                            Hiç taksit grubu bulunamadı.
                            {canAddGroup && (
                                <Button
                                    color="success"
                                    size="sm"
                                    className="ms-3"
                                    onClick={() => setAddGroupModalOpen(true)}
                                >
                                    <i className="bx bx-plus me-1"></i>
                                    Yeni Grup Ekle
                                </Button>
                            )}
                        </Alert>
                    ) : (
                        <Form id="instantPaymentForm" onSubmit={handleSubmit}>
                            <FormGroup>
                                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                  <Label className="mb-0">Taksit Grubu</Label>
                                  <div className="d-flex align-items-center gap-2">
                                    {/* Masaüstü: butonun yanında kısa not */}
                                    <small className="text-muted d-none d-sm-inline">
                                      Eklediğiniz grup kendi firmanıza eklenir. Henüz atanmadığı için cari seçildiğinde görünmez.
                                    </small>
                                    {canAddGroup && (
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => setAddGroupModalOpen(true)}
                                        style={{ borderRadius: "0" }}
                                      >
                                        <i className="bx bx-plus me-1"></i>
                                        Yeni Grup Ekle
                                      </Button>
                                    )}
                                  </div>
                                  {/* Mobil: notu alt satıra düşür */}
                                  <small className="text-muted d-sm-none">
                                    Eklediğiniz grup kendi firmanıza eklenir. Henüz atanmadığı için cari seçildiğinde görünmez.
                                  </small>
                                </div>

                                <Input
                                    type="select"
                                    name="accountGroupId"
                                    onChange={formik.handleChange}
                                    value={formik.values.accountGroupId}
                                    disabled={filteredGroups.length === 0}
                                    invalid={
                                        formik.touched.accountGroupId &&
                                        !!formik.errors.accountGroupId
                                    }
                                >
                                    <option value="">Seçiniz</option>
                                    {filteredGroups.map((g) => (
                                        <option key={g.id} value={g.id}>
                                            {g.groupName}
                                        </option>
                                    ))}
                                </Input>
                                {filteredGroups.length === 0 &&
                                    formik.values.billingOption === "existing_account" && (
                                        <small className="text-danger">
                                            {selectedAccount && !canAddGroup
                                                ? "Bu cari/bayiye atanmış taksit grubu yok. Firma yöneticinizden atama isteyin."
                                                : "Seçilen cari/bayi için atalı bir taksit grubu yok."}
                                        </small>
                                    )}
                                {formik.touched.accountGroupId && (
                                    <FormFeedback>{formik.errors.accountGroupId}</FormFeedback>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>Bitiş Tarihi</Label>
                                <DateTimeInput
                                    value={formik.values.expiryDate}
                                    onChange={(v) => formik.setFieldValue("expiryDate", v)}
                                    invalid={
                                        formik.touched.expiryDate && !!formik.errors.expiryDate
                                    }
                                    minDate={nowLocalDateTime()}
                                />
                                {formik.touched.expiryDate && (
                                    <FormFeedback>{formik.errors.expiryDate}</FormFeedback>
                                )}
                            </FormGroup>

                            <div className="d-flex justify-content-between justify-content-sm-end my-2">
                                <Button
                                    color="secondary"
                                    size="sm"
                                    outline
                                    onClick={() => setShowRates((s) => !s)}
                                    disabled={!formik.values.accountGroupId}
                                    title="Seçili grubun taksit oranlarını görüntüle"
                                >
                                    <i
                                        className={`bx ${showRates ? "bx-hide" : "bx-show"} me-1`}
                                    />
                                    {showRates
                                        ? "Taksit Oranlarını Gizle"
                                        : "Taksit Oranlarını Göster"}
                                </Button>
                            </div>

                            {!showRates && formik.values.accountGroupId && (
                                <Alert color="info" className="py-2">
                                    Taksit oranlarını görmek için{" "}
                                    <strong>“Taksit Oranlarını Göster”</strong> düğmesine
                                    tıklayın.
                                </Alert>
                            )}

                            <Collapse isOpen={!!formik.values.accountGroupId && showRates}>
                                <InstallmentRateCards group={currentGroup} />
                            </Collapse>

                            <FormGroup className="mt-3">
                                <Label>Sabit Tutar (opsiyonel)</Label>
                                <div className="input-group">
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        name="fixedAmount"
                                        value={displayFixedAmount}
                                        onChange={handleFixedAmountChange}
                                        invalid={
                                            formik.touched.fixedAmount &&
                                            !!formik.errors.fixedAmount
                                        }
                                        placeholder="Örn: 1.250,50"
                                    />
                                    <span className="input-group-text">₺</span>
                                </div>
                                {formik.touched.fixedAmount && formik.errors.fixedAmount && (
                                    <div
                                        className="invalid-feedback"
                                        style={{ display: "block" }}
                                    >
                                        {formik.errors.fixedAmount}
                                    </div>
                                )}
                                <small className="text-muted">
                                    Doldurursanız ödeme sayfasında tutar değiştirilemez.
                                </small>
                            </FormGroup>

                            {link && (
                                <Alert color="success" className="mt-3">
                                    <i className="bx bx-check-circle me-2"></i>
                                    <strong>Bağlantı Oluşturuldu:</strong> {link}
                                    <div className="mt-2 d-flex flex-column flex-sm-row justify-content-end gap-2">
                                        <Button
                                            color="success"
                                            size="sm"
                                            href={link}
                                            target="_blank"
                                            className="me-2"
                                        >
                                            <i className="bx bx-link-external me-1" /> Aç
                                        </Button>
                                        <Button
                                            color="outline-success"
                                            size="sm"
                                            onClick={() => navigator.clipboard.writeText(link)}
                                        >
                                            <i className="bx bx-copy me-1" /> Kopyala
                                        </Button>
                                    </div>
                                </Alert>
                            )}

                            <div className="row align-items-center">
                                <div className="col-md-8">
                                    {/* API hatalarını veya submit hatasını göster */}
                                    {(error || groupsError) && (
                                        <Alert color="danger" className="mb-md-0 mb-3">
                                            <div className="d-flex align-items-center">
                                                <i className="bx bx-error-circle me-2 fs-5"></i>
                                                <div>
                                                    <strong>Hata:</strong> {error || groupsError}
                                                </div>
                                            </div>
                                        </Alert>
                                    )}
                                </div>
                                <div className="col-md-4 text-end" />
                            </div>
                        </Form>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        form="instantPaymentForm"
                        type="submit"
                        disabled={
                            loadingGroups ||
                            !flowChosen ||
                            !formik.values.accountGroupId ||
                            formik.isSubmitting
                        }
                    >
                        {formik.isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Oluşturuluyor...
                            </>
                        ) : (
                            <>
                                <i className="bx bx-plus-circle me-2" />
                                Bağlantı Oluştur
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Yeni Grup Ekleme Modal'ı artık dışarıdan çağrılıyor */}
            <AddGroupModal
                isOpen={addGroupModalOpen}
                toggle={() => setAddGroupModalOpen(false)}
                companyId={companyId}
                isMobile={isMobile}
                onSuccess={() => {
                    setAddGroupModalOpen(false);
                    refetchGroups(); // Grupları yeniden çek
                }}
            />
        </>
    );
}